import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from '../../i18n/useTranslation';
import { useAuth } from '../../context/AuthContext';
import { Alert } from '../atoms/Alert/Alert';
import { Badge } from '../atoms/Badge/Badge';
import { usePendingApprovals } from './usePendingApprovals';
import './AdminArea.css';

/** The admin area's sections — one route each (deep-linkable, audited per route). */
export const ADMIN_TABS = [
  { id: 'users', to: '/admin/users', labelKey: 'adminArea.tabs.users' },
  { id: 'licenses', to: '/admin/licenses', labelKey: 'adminArea.tabs.licenses' },
  { id: 'ai-usage', to: '/admin/ai-usage', labelKey: 'adminArea.tabs.aiUsage' },
  { id: 'analytics', to: '/admin/analytics', labelKey: 'adminArea.tabs.analytics' },
  { id: 'gdpr', to: '/admin/gdpr', labelKey: 'adminArea.tabs.gdpr' },
  { id: 'release-notes', to: '/admin/release-notes', labelKey: 'adminArea.tabs.releaseNotes' },
  { id: 'feedback', to: '/admin/feedback', labelKey: 'adminArea.tabs.feedback' },
  { id: 'opt-ins', to: '/admin/opt-ins', labelKey: 'adminArea.tabs.optIns' },
  { id: 'voice-quality', to: '/admin/voice-quality', labelKey: 'adminArea.tabs.voiceQuality' },
] as const;

/** True when the signed-in user is in the Cognito 'admin' group (id token claim). */
export function useIsAdmin(): boolean {
  const { state } = useAuth();
  return state.user?.groups?.includes('admin') ?? false;
}

export interface AdminAreaProps {
  children: ReactNode;
}

/**
 * Body of every admin page, rendered inside the page's
 * PageContent › PageHeader: the section navigation (links styled as tabs,
 * aria-current on the active one) and the section content. Non-admins see an
 * explanation instead — the API refuses them anyway (requireRole('admin')),
 * this only avoids a page full of 403 errors.
 */
export function AdminArea({ children }: AdminAreaProps) {
  const { t } = useTranslation();
  const isAdmin = useIsAdmin();
  // Closed beta: accounts waiting for approval — count badge on the "Users" tab.
  const { count: pendingCount } = usePendingApprovals(isAdmin);

  if (!isAdmin) {
    return (
      <Alert tone="warning" live="off" title={t('adminArea.forbiddenTitle')}>
        {t('adminArea.forbiddenMessage')}
      </Alert>
    );
  }

  return (
    <>
      <nav className="rg-admin-area__tabs" aria-label={t('adminArea.tabsLabel')}>
        <ul className="rg-admin-area__tab-list">
          {ADMIN_TABS.map((tab) => (
            <li key={tab.id}>
              <NavLink
                to={tab.to}
                className={({ isActive }) =>
                  ['rg-admin-area__tab', isActive ? 'rg-admin-area__tab--active' : ''].filter(Boolean).join(' ')
                }
              >
                {t(tab.labelKey)}
                {tab.id === 'users' && pendingCount > 0 && (
                  <Badge tone="warning" className="rg-admin-area__tab-badge">
                    <span aria-hidden="true">{pendingCount}</span>
                    <span className="rg-admin-area__sr-only">
                      {t('adminArea.users.approval.tabBadge', { count: pendingCount })}
                    </span>
                  </Badge>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
      <div className="rg-admin-area__content">{children}</div>
    </>
  );
}

export default AdminArea;
